import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Checkbox, Empty, Select, Space, Spin, Tag, Typography } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { getRegionalRecommendations } from '../../api/regionalRecommendations';
import type { Product } from '../../types/product';
import type { RegionalRecommendationItem, RegionalRecommendationResponse } from '../../types/regionalRecommendation';
import ProductDetailDrawer from '../products/components/ProductDetailDrawer';
import './index.css';

const { Text, Title } = Typography;

const knownRegions = [
  '北京',
  '天津',
  '上海',
  '重庆',
  '河北',
  '山西',
  '辽宁',
  '吉林',
  '黑龙江',
  '江苏',
  '浙江',
  '安徽',
  '福建',
  '江西',
  '山东',
  '河南',
  '湖北',
  '湖南',
  '广东',
  '海南',
  '四川',
  '贵州',
  '云南',
  '陕西',
  '甘肃',
  '青海',
  '台湾',
  '内蒙古',
  '广西',
  '西藏',
  '宁夏',
  '新疆',
  '香港',
  '澳门',
];

const sourceLabels: Record<string, string> = {
  haoyi: '号易',
  '172': '172平台',
  manual: '手动',
};

const operatorColors: Record<string, string> = {
  移动: 'blue',
  电信: 'orange',
  联通: 'green',
  广电: 'purple',
};

const regionOptions = knownRegions.map((region) => ({ label: region, value: region }));

const limitOptions = [1, 2, 3, 5].map((value) => ({
  label: `每运营商 ${value} 张`,
  value,
}));

const formatNullable = (value?: string | number | null, suffix = '') => {
  if (value === null || value === undefined || value === '') return '-';
  return `${value}${suffix}`;
};

const renderLimitedTags = (values: string[], maxVisible = 18) => {
  const visibleValues = values.slice(0, maxVisible);
  const hiddenCount = Math.max(values.length - visibleValues.length, 0);

  return (
    <>
      {visibleValues.map((value) => (
        <Tag key={value}>{value}</Tag>
      ))}
      {hiddenCount > 0 && <Tag color="default">另 {hiddenCount} 个</Tag>}
    </>
  );
};

const RegionalRecommendationsPage: React.FC = () => {
  const { message } = App.useApp();
  const [selectedRegions, setSelectedRegions] = useState<string[]>(['江西']);
  const [allRegions, setAllRegions] = useState(false);
  const [limitPerOperator, setLimitPerOperator] = useState(2);
  const [data, setData] = useState<RegionalRecommendationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchRecommendations = useCallback(async () => {
    if (!allRegions && selectedRegions.length === 0) {
      message.warning('请选择至少一个地区，或勾选全国');
      return;
    }

    try {
      setLoading(true);
      const response = await getRegionalRecommendations(selectedRegions, allRegions, limitPerOperator);
      setData(response);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取地区推荐失败');
    } finally {
      setLoading(false);
    }
  }, [allRegions, limitPerOperator, message, selectedRegions]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const openDetail = (product: Product) => {
    setDetailProduct(product);
    setDetailVisible(true);
  };

  const closeDetail = () => {
    setDetailVisible(false);
  };

  const renderRecommendation = (item: RegionalRecommendationItem) => {
    const product = item.product;
    const appearanceLabels = item.appearances.map(
      (appearance) => `${appearance.region}-${appearance.operator} #${appearance.rank}`,
    );

    return (
      <div className="recommendation-card" key={product.id}>
        <div className="recommendation-card-header">
          <div className="recommendation-title-wrap">
            <Text strong className="recommendation-title">{product.name || '-'}</Text>
            <Space size={[6, 6]} wrap className="recommendation-tags">
              <Tag color={product.source_platform === '172' ? 'orange' : 'blue'}>
                {sourceLabels[product.source_platform || ''] || product.source_platform || '-'}
              </Tag>
              <Tag color={operatorColors[product.operator || ''] || 'default'}>{product.operator || '-'}</Tag>
              <Tag color="cyan">推荐分 {item.score}</Tag>
              <Tag color="geekblue">覆盖 {item.coverage_count} 省</Tag>
            </Space>
          </div>
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openDetail(product)}
          />
        </div>

        <div className="recommendation-metrics">
          <span>月租 {formatNullable(item.metrics.monthly, '元')}</span>
          <span>通用 {formatNullable(item.metrics.generalTraffic, 'G')}</span>
          <span>定向 {formatNullable(item.metrics.directionalTraffic, 'G')}</span>
          <span>通话 {formatNullable(item.metrics.voice, '分钟')}</span>
          <span>结算 {item.metrics.settlePrice ? `¥${item.metrics.settlePrice}` : '-'}</span>
        </div>

        <Text type="secondary" className="recommendation-scope">{item.available_scope}</Text>

        <div className="recommendation-block">
          <Text type="secondary">覆盖省份</Text>
          <div className="recommendation-tag-list">
            {renderLimitedTags(item.covered_regions, 24)}
          </div>
        </div>

        <div className="recommendation-block">
          <Text type="secondary">占用席位</Text>
          <div className="recommendation-tag-list">
            {renderLimitedTags(appearanceLabels, 24)}
          </div>
        </div>

        <div className="recommendation-block">
          <Text type="secondary">推荐理由</Text>
          <div className="recommendation-tag-list">
            {item.score_reasons.slice(0, 8).map((reason) => (
              <Tag key={reason}>{reason}</Tag>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="regional-recommendations-page">
      <div className="regional-recommendations-toolbar">
        <div>
          <Title level={4}>地区推荐</Title>
          <Text type="secondary">
            先按省份和运营商选推荐席位，再按产品合并展示覆盖省份。
          </Text>
        </div>
        <Space wrap align="center">
          <Checkbox checked={allRegions} onChange={(event) => setAllRegions(event.target.checked)}>
            全国
          </Checkbox>
          <Select
            mode="multiple"
            value={selectedRegions}
            options={regionOptions}
            onChange={setSelectedRegions}
            className="region-select"
            showSearch
            disabled={allRegions}
            optionFilterProp="label"
            maxTagCount="responsive"
            placeholder="选择省份"
          />
          <Select
            value={limitPerOperator}
            options={limitOptions}
            onChange={setLimitPerOperator}
            className="limit-select"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchRecommendations} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      {data && (
        <div className="recommendation-summary">
          <span>地区 {data.regions.length} 个</span>
          <span>席位 {data.filledSlots}/{data.totalSlots}</span>
          <span>去重后 {data.uniqueProducts} 张卡</span>
        </div>
      )}

      <Spin spinning={loading}>
        <div className="recommendation-list">
          {data?.items?.length ? (
            data.items.map(renderRecommendation)
          ) : (
            <Empty description="暂无推荐数据" />
          )}
        </div>
      </Spin>

      <ProductDetailDrawer
        open={detailVisible}
        product={detailProduct}
        platform172Detail={null}
        zIndex={1200}
        onClose={closeDetail}
      />
    </div>
  );
};

export default RegionalRecommendationsPage;
